"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  diatonicChordsForKey,
  type DiatonicChord,
} from "@/lib/music/chord-catalog";
import {
  pitchClassToName,
  QUALITY_INTERVALS,
  ROOT_PITCH_CLASS,
} from "@/lib/music/theory-helpers";
import {
  BORDER,
  BORDER_HI,
  FONT_MONO,
  SURFACE,
  SURFACE_LO,
  TEXT,
  TEXT_DIM,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "@/components/ui/organisms/chord-practice/palette";
import { useActivePianoNotes } from "@/hooks/use-active-piano-notes";
import { useMidiSetHit } from "@/hooks/use-midi-set-hit";
import { VolumeIcon } from "@/components/ui/atoms/icons";
import { pcToMidiInOctave } from "@/lib/music/theory-helpers";
import {
  DIATONIC_FUNCTION,
  FUNCTION_COLOR,
  FUNCTION_LABEL,
  FUNCTION_NARRATIVE,
  POPULAR_PROGRESSIONS,
  PROGRESSION_VOICINGS,
  type PopularProgression,
  type ProgressionVoicing,
} from "./harmony-data";
import { ProgressionStage, type ProgressionSlot } from "./progression-stage";
import {
  ProgressionTransition,
  type TransitionSlot,
} from "./progression-transition";
import { useProgressionPianoHints } from "./use-progression-piano-hints";
import { useVoicingPreview } from "./use-voicing-preview";
import { useAutoAdvance } from "./use-auto-advance";
import type { HarmonyTonality } from "./types";

/** True when the tonic spelling implies flats over sharps. */
const FLAT_TONICS = new Set(["F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb"]);

const INVERSION_LABELS: Record<number, string> = {
  0: "fondamentale",
  1: "1er renv.",
  2: "2e renv.",
};

/**
 * Inspect the lowest upper voice of a transposed voicing to determine the
 * shape of the right hand : root position, 1st or 2nd inversion.
 *
 * The left-hand bass is always the root in our voicings, so this describes
 * the **hand**, never the chord's bass. It is deliberately not rendered as
 * slash notation : `Cmaj/E` names the bass everywhere else in music, and
 * using it for the right hand would teach the learner to misread a symbol
 * they will meet in the wild.
 */
function describeInversion(
  voices: number[],
  rootPc: number,
  triadIntervals: number[],
): { inversionLabel: string } {
  const lowestUpperPc = (((voices[0] ?? rootPc) % 12) + 12) % 12;
  const intervalFromRoot = (lowestUpperPc - rootPc + 12) % 12;
  const idx = triadIntervals.indexOf(intervalFromRoot);

  if (idx <= 0) {
    return { inversionLabel: INVERSION_LABELS[0]! };
  }

  return { inversionLabel: INVERSION_LABELS[idx] ?? "renversement" };
}

/**
 * Transpose a C-major reference voicing to the given tonality.
 *
 * Picks the smallest signed offset (in `[-5, +6]`) so the voices stay in
 * a comfortable register regardless of the key. Mirrors the helper that
 * lives in the mockup `progression-drill.tsx`, kept colocated here
 * because it is the only call site.
 */
function transposeVoicing(
  voicing: ProgressionVoicing,
  tonicPc: number,
): ProgressionVoicing {
  let offset = tonicPc;

  if (offset > 6) {
    offset -= 12;
  }

  return {
    bass: voicing.bass + offset,
    voices: voicing.voices.map((v) => v + offset),
  };
}

interface ProgressionDrillProps {
  tonality: HarmonyTonality;
  progressionId: string;
  /**
   * Reserved : kept for parity with the previous variants strip API.
   * Unused for now, the user navigates via the Atlas (sidebar).
   */
  onPickProgression?: (id: string) => void;
}

/**
 * Detail view for a popular progression. Two rendering modes:
 *
 * - in major mode with hand-picked voicings: `ProgressionTransition`
 *   illustrates voice leading between the current and the next chord;
 * - otherwise (minor mode, or no voicing data): `ProgressionStage`
 *   falls back to the simpler sliding-queue visualisation.
 *
 * The studio piano is decorated by `useProgressionPianoHints` (called
 * inside this component, scoped to the current step), no embedded
 * piano dock here, the studio piano is the only piano.
 */
export function ProgressionDrill({
  tonality,
  progressionId,
}: ProgressionDrillProps) {
  const progression = POPULAR_PROGRESSIONS.find((p) => p.id === progressionId);

  const diatonic = useMemo(
    () => diatonicChordsForKey(tonality.tonic, tonality.mode),
    [tonality],
  );

  const chords = useMemo<DiatonicChord[]>(() => {
    if (!progression) {
      return [];
    }

    return progression.degrees
      .map((d) => diatonic[d])
      .filter((c): c is DiatonicChord => Boolean(c));
  }, [progression, diatonic]);

  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
  }, [progressionId, tonality.tonic, tonality.mode]);

  const tonicPc = ROOT_PITCH_CLASS[tonality.tonic] ?? 0;

  const slots = useMemo<ProgressionSlot[]>(
    () =>
      chords.map((c) => {
        const root = c.entry.root;
        const rootPc = ROOT_PITCH_CLASS[root] ?? 0;
        const intervals = QUALITY_INTERVALS[c.entry.quality] ?? [0, 4, 7];
        const triadIntervals = intervals.slice(0, 3);
        const notes = triadIntervals.map((iv) =>
          pitchClassToName((rootPc + iv) % 12, root),
        );
        const pcs = triadIntervals.map((iv) => (rootPc + iv) % 12);

        return {
          notes,
          rootPc,
          pcs,
          roman: c.roman,
          symbol: c.entry.symbol,
        };
      }),
    [chords],
  );

  /**
   * Transition slots, populated only for major-mode progressions for
   * which we have hand-picked voicings. When unavailable we fall back
   * to the simpler `ProgressionStage` (sliding queue).
   */
  const transitionSlots = useMemo<TransitionSlot[] | null>(() => {
    if (tonality.mode !== "major") {
      return null;
    }

    const refVoicings = PROGRESSION_VOICINGS[progressionId];

    if (!refVoicings || refVoicings.length !== chords.length) {
      return null;
    }

    if (!progression) {
      return null;
    }

    return chords.map((c, i) => {
      const v = transposeVoicing(refVoicings[i]!, tonicPc);
      const degree = progression.degrees[i] ?? 0;
      const fnTag = DIATONIC_FUNCTION[degree] ?? "T";
      const functionColor = FUNCTION_COLOR[fnTag] ?? VIOLET_BRIGHT;

      const chordRootPc = ROOT_PITCH_CLASS[c.entry.root] ?? 0;
      const chordIntervals =
        QUALITY_INTERVALS[c.entry.quality] ?? [0, 4, 7];
      const triadIntervals = chordIntervals.slice(0, 3);
      const { inversionLabel } = describeInversion(
        v.voices,
        chordRootPc,
        triadIntervals,
      );

      return {
        roman: c.roman,
        symbol: c.entry.symbol,
        functionColor,
        inversionLabel,
        bass: v.bass,
        voices: v.voices,
      };
    });
  }, [chords, progression, progressionId, tonality.mode, tonicPc]);

  const useFlats = FLAT_TONICS.has(tonality.tonic);

  // ─── Auto-advance state machine ─────────────────────────────────
  //
  // The progression view supports a hands-free workflow : when the
  // student plays the full target voicing (octave-strict MIDI), the
  // current chord lights up, holds for 80 ms, confirms over 400 ms,
  // then plays a 440 ms column-swap animation before moving on.
  //
  // The state machine lives in `useAutoAdvance` ; we only feed it
  // (`complete`) and react to its output (`isConfirming`, `isTransitioning`).

  const currentVoicingMidis = useMemo<number[]>(() => {
    const v = transitionSlots?.[step];

    if (!v) {
      return [];
    }

    return [v.bass, ...v.voices];
  }, [transitionSlots, step]);

  const rawComplete = useMidiSetHit(currentVoicingMidis);

  // ─── Listen-to-chord preview ────────────────────────────────────
  //
  // Plays the current chord on the studio piano for ~900 ms. When a
  // hand-picked voicing is available we play it as-is (LH bass + RH
  // voices) ; otherwise we fall back to the triad pcs placed around
  // C4-C5, same heuristic as `useChordPreview`.
  //
  // The studio piano emits real `press` events for these notes, so
  // `useMidiSetHit` would mark `complete = true` and the auto-advance
  // machine would skip to the next chord. We mask `complete` while the
  // preview is running so listening stays purely passive.
  const { play: previewVoicing, isPreviewing } = useVoicingPreview();
  const complete = rawComplete && !isPreviewing;

  const handleAdvance = useCallback(() => {
    setStep((s) => (s + 1) % Math.max(chords.length, 1));
  }, [chords.length]);

  const {
    isConfirming,
    isTransitioning,
    triggerManual,
    reset: resetAutoAdvance,
  } = useAutoAdvance({
    complete,
    chordCount: chords.length,
    onAdvance: handleAdvance,
  });

  // Reset the state machine when the progression / tonality changes,
  // so a stale `confirming`/`transitioning` doesn't leak into the
  // newly mounted slots.
  useEffect(() => {
    resetAutoAdvance();
  }, [progressionId, tonality.tonic, tonality.mode, resetAutoAdvance]);

  // Piano hints: when a voicing is available, light up the actual played
  // MIDI notes (LH bass + RH voices) so the student sees the spread of
  // the hands on the keyboard. Otherwise fall back to a single-octave
  // collapse (minor mode without hand-picked voicings).
  //
  // Note about the auto-advance transition : `step` is incremented at
  // the phase 1 → 2 boundary (i.e. `t = 220 ms` after entering
  // `transitioning`). That coincides with the visual moment the new
  // left column starts fading in, so the keyboard hints flip *with*
  // the new column, not before, no 220 ms «&nbsp;dark keyboard&nbsp;»
  // gap, no extra delay machinery needed.
  const pianoHints = useMemo(() => {
    const current = slots[step];

    if (!current) {
      return null;
    }

    const voicing = transitionSlots?.[step];
    const voicingMidi = voicing
      ? [voicing.bass, ...voicing.voices]
      : undefined;

    return {
      chordPcs: current.pcs,
      rootPc: current.rootPc,
      voicingMidi,
    };
  }, [slots, step, transitionSlots]);

  useProgressionPianoHints(pianoHints, true);

  // Pressed MIDI subset relevant to the *current* voicing. We pass the
  // raw active set down ; `ProgressionTransition` filters on its own
  // pastilles. (Cheap : the set is already memoised by `useActivePianoNotes`.)
  const pressedMidis = useActivePianoNotes();

  const handleManualStep = useCallback(
    (pickStep: number) => {
      if (isTransitioning) {
        return;
      }

      // The visual transition handles the step swap itself at t=220ms.
      // We override `onAdvance` for one tick so the machine bumps to
      // `pickStep` rather than `step + 1`.
      const delta = ((pickStep - step) % chords.length + chords.length) %
        chords.length;

      if (delta === 0) {
        return;
      }

      // Cheap path : if the pick is the next chord, the default
      // `onAdvance` is already correct.
      if (delta === 1) {
        triggerManual();

        return;
      }

      // Otherwise we step directly without animation. Skipping the
      // animation here is intentional : a multi-step jump shouldn't
      // pretend to be a voice-leading transition.
      setStep(pickStep);
    },
    [chords.length, isTransitioning, step, triggerManual],
  );

  const handlePrev = useCallback(() => {
    if (isTransitioning) {
      return;
    }

    // Manual prev bypasses the auto-advance state machine ; the
    // filmstrip handles the backward animation natively via its CSS
    // transform transition (interpolates the offset both ways).
    setStep((s) => (s === 0 ? Math.max(chords.length - 1, 0) : s - 1));
    resetAutoAdvance();
  }, [chords.length, isTransitioning, resetAutoAdvance]);

  const handleNext = useCallback(() => {
    triggerManual();
  }, [triggerManual]);

  const handleReset = useCallback(() => {
    setStep(0);
    resetAutoAdvance();
  }, [resetAutoAdvance]);

  const handleListen = useCallback(() => {
    const voicing = transitionSlots?.[step];

    if (voicing) {
      previewVoicing([voicing.bass, ...voicing.voices]);

      return;
    }

    const slot = slots[step];

    if (!slot) {
      return;
    }

    previewVoicing(slot.pcs.map((pc) => pcToMidiInOctave(pc)));
  }, [previewVoicing, slots, step, transitionSlots]);

  if (!progression || chords.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 40,
          background: SURFACE_LO,
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: TEXT_DIM,
            maxWidth: 360,
            textAlign: "center",
            lineHeight: 1.55,
          }}
        >
          Aucune progression sélectionnée. Reviens à la vue d&apos;ensemble
          pour en choisir une.
        </p>
      </div>
    );
  }

  return (
    // `height: 100%` (rather than `flex: 1`) because the harmony slot
    // in `harmony-overlay` is not a flex container — same pattern as
    // `HarmonyScaleDetail`. Without this, the inner filmstrip falls
    // back to its content height and overflows the slot, leaking under
    // the studio piano.
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none"
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse at 50% 50%, ${VIOLET_SOFT} 0%, transparent 55%)`,
        }}
      />

      <Header
        progression={progression}
        chords={chords}
        step={step}
        onPickStep={handleManualStep}
        onPrev={handlePrev}
        onNext={handleNext}
        onReset={handleReset}
        onListen={handleListen}
      />

      <FunctionStrip progression={progression} chords={chords} step={step} />

      {transitionSlots ? (
        <ProgressionTransition
          step={step}
          slots={transitionSlots}
          useFlats={useFlats}
          pressedMidis={isTransitioning ? undefined : pressedMidis}
          complete={!isTransitioning && complete}
          confirming={isConfirming}
        />
      ) : (
        <ProgressionStage slots={slots} currentStep={step} />
      )}
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────

function Header({
  progression,
  chords,
  step,
  onPickStep,
  onPrev,
  onNext,
  onReset,
  onListen,
}: {
  progression: PopularProgression;
  chords: DiatonicChord[];
  step: number;
  onPickStep: (i: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onReset: () => void;
  onListen: () => void;
}) {
  return (
    <header
      className="relative z-10 flex shrink-0 items-center justify-between"
      style={{
        padding: "16px 24px",
        borderBottom: `1px solid ${BORDER}`,
        gap: 24,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              color: TEXT,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {progression.numerals}
          </h1>
          <span
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 12,
              color: TEXT_DIM,
              lineHeight: 1.4,
            }}
          >
            <strong style={{ color: TEXT }}>{progression.label}</strong> , {" "}
            {progression.context}
          </span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <MiniStrip chords={chords} step={step} onPickStep={onPickStep} />
        <ListenButton onClick={onListen} />
        <ChevronButton dir="prev" onClick={onPrev} />
        <ChevronButton dir="next" onClick={onNext} />
        <ResetButton onClick={onReset} disabled={step === 0} />
      </div>
    </header>
  );
}

function MiniStrip({
  chords,
  step,
  onPickStep,
}: {
  chords: DiatonicChord[];
  step: number;
  onPickStep: (i: number) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        padding: 4,
        borderRadius: 8,
        background: SURFACE_LO,
        border: `1px solid ${BORDER}`,
      }}
    >
      {chords.map((c, i) => {
        const isActive = i === step;
        const isDone = i < step;

        return (
          <button
            key={`${c.entry.id}-${i}`}
            type="button"
            onClick={() => onPickStep(i)}
            aria-label={`Aller à l'étape ${i + 1} (${c.roman})`}
            style={{
              padding: "4px 10px",
              borderRadius: 4,
              border: "none",
              fontFamily: FONT_MONO,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.04em",
              cursor: "pointer",
              background: isActive ? VIOLET : "transparent",
              color: isActive ? "#fff" : isDone ? TEXT_DIM : TEXT,
              opacity: isDone ? 0.55 : 1,
              boxShadow: isActive ? `0 0 8px ${VIOLET_GLOW}` : "none",
              transition: "all 120ms",
            }}
          >
            {c.roman}
          </button>
        );
      })}
    </div>
  );
}

function ChevronButton({
  dir,
  onClick,
}: {
  dir: "prev" | "next";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === "prev" ? "Précédent" : "Suivant"}
      style={{
        width: 30,
        height: 30,
        borderRadius: 99,
        background: SURFACE,
        border: `1px solid ${BORDER_HI}`,
        color: TEXT,
        fontSize: 14,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
        paddingBottom: 2,
      }}
    >
      {dir === "prev" ? "‹" : "›"}
    </button>
  );
}

function ListenButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Écouter l'accord courant"
      style={{
        width: 30,
        height: 30,
        borderRadius: 99,
        background: SURFACE,
        border: `1px solid ${BORDER_HI}`,
        color: TEXT,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
      }}
    >
      <VolumeIcon size={14} />
    </button>
  );
}

function ResetButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Reprendre depuis le début"
      style={{
        width: 30,
        height: 30,
        borderRadius: 99,
        background: SURFACE,
        border: `1px solid ${BORDER_HI}`,
        color: TEXT,
        fontSize: 13,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.35 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
      }}
    >
      ↻
    </button>
  );
}

// ─── Function strip (théorie courante) ─────────────────────────────────

function FunctionStrip({
  progression,
  chords,
  step,
}: {
  progression: PopularProgression;
  chords: DiatonicChord[];
  step: number;
}) {
  const fnTag = DIATONIC_FUNCTION[progression.degrees[step] ?? 0] ?? "T";
  const fnColor = FUNCTION_COLOR[fnTag] ?? VIOLET_BRIGHT;
  const fnLabel = FUNCTION_LABEL[fnTag] ?? "Tonique";
  const fnNarrative = FUNCTION_NARRATIVE[fnTag] ?? "";
  const currentRoman = chords[step]?.roman ?? "";

  return (
    <section
      className="relative z-10 flex shrink-0 items-center"
      style={{
        padding: "12px 24px",
        borderBottom: `1px solid ${BORDER}`,
        background: "rgba(10, 10, 14, 0.6)",
        gap: 14,
      }}
    >
      <span
        style={{
          padding: "4px 12px",
          borderRadius: 6,
          background: `${fnColor.replace("0.85", "0.16")}`,
          border: `1px solid ${fnColor.replace("0.85", "0.4")}`,
          fontFamily: FONT_MONO,
          fontSize: 11,
          color: fnColor,
          letterSpacing: "0.12em",
          fontWeight: 700,
          textTransform: "uppercase",
          flexShrink: 0,
        }}
      >
        {fnTag} · {fnLabel}
      </span>
      <span
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 13,
          color: TEXT_DIM,
          lineHeight: 1.5,
        }}
      >
        Le degré <strong style={{ color: TEXT }}>{currentRoman}</strong>{" "}
        joue ici le rôle de {fnLabel.toLowerCase()} , {" "}
        {fnNarrative.toLowerCase()}.
      </span>
    </section>
  );
}
